import { API_BASE_URL } from '@synergy/config';
import { Box, Divider, Theme } from '@mui/material';
import { Component, ComponentProps, ReactElement, createRef } from 'react';
import {
    DocumentStatus,
    DocumentType,
    ObjectWithId,
    PurchaseOrderTransition,
    ReceiptAlert,
    UserRole,
} from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { PaloDocumentPreview, PaloHistory, PaloRecordPager, PaloShortcutBinding } from '@synergy/components-palo';
import { PaloThemeProvider } from '@synergy/theme-palo';
import { PoolSpinner, WithRouterProps, withRouter } from '@synergy/components';
import { WithStyles, createStyles, withStyles } from '@mui/styles';
import { changePurchaseOrderToCredit, getPurchaseOrderStatusTranslationId, hasRole, padZeroLeft } from '@synergy/utils';
import { connect } from 'react-redux';
import { internalMediaSelector, resetUploads, supplierMediaSelector } from '@synergy/data-access';
import urljoin from 'url-join';

import { AppState } from '../../../store/models/app-state';
import { ConvertOutcome } from '../../convert-receipt/models/convert-outcome';
import { DetailStream } from '../../activity-sidebar/details/detail-stream';
import { PaloCompleteDocumentDialog } from '../../../common/components/palo/palo-complete-document-dialog';
import { PaloDetailHeader } from '../../detail-header/palo-detail-header';
import { PaloDirtyProvider } from '../../../common/components/palo/palo-dirty-context';
import { PaloDocumentChain, PaloDocumentChainTabName } from '../../document-chain/palo-document-chain';
import { PaloDocumentChainCreate, PaloDocumentChainProvider } from '../../document-chain/palo-document-chain-context';
import { PaloDocumentPhase, PaloFlowAction, paloDocumentPhase } from '../../detail-header/palo-main-action';
import { PaloDocumentTitle } from '../../../common/components/palo/palo-document-title';
import { PaloInPositionsSlot, PaloPositionsSlotProvider } from '../../../common/components/palo/palo-positions-slot';
import { PaloPurchaseOrderAttachments } from './palo-purchase-order-attachments';
import { PaloPurchaseOrderDetailOverview } from '../containers/purchase-order-detail-overview/palo/palo-purchase-order-detail-overview';
import { PaloPurchaseOrderPositionTools } from './palo-purchase-order-position-tools';
import { PaloRecordDock } from '../../../common/components/palo/palo-record-dock';
import { PaloStatusChangeDialog } from '../../../common/components/palo/palo-status-change-dialog';
import { PoolStep } from '../../../common/models/step';
import { PurchaseOrder } from '../models/purchaseOrder';
import { PurchaseOrderDetailPositions } from '../containers/purchase-order-detail-positions/purchase-order-detail-positions';
import { PurchaseOrderDetailProvider } from '../containers/context/purchase-order-context';
import { PurchaseOrderSteps } from '../models/purchaseOrder-steps';
import { ROUTES } from '../../sidebar-menu/navigation-drawer/routes';
import { ReceiptNoteType } from '../../../common/models/enums/receipt-note-type';
import { Transition } from '../../convert-receipt/models/type-transition';
import { TransitionTarget } from '../../convert-receipt/models/internal/transition-target';
import { User } from '../../../common/models/user';
import { activitiesSelector } from '../../../common/selectors/activity-selectors';
import {
    completePurchaseOrder,
    createOrUpdatePurchaseOrder,
    deletePurchaseOrder,
    deleteReceiptAlert,
    fetchPurchaseOrder,
    reportPurchaseOrderSaveFailed,
    resetPurchaseOrderState,
    updateReceiptAlert,
} from '../purchase-order-actions';
import {
    fetchActivities,
    resetActivities,
    resetDocumentType,
    setDocumentType,
} from '../../../common/actions/activity-actions';
import { fetchAllTaxRates } from '../../configuration/finance/tax-rates/store/tax-rates-actions';
import { fetchReceiptInternalNotes, resetReceiptInternalNotes } from '../../../common/actions/internal-notes-actions';
import { fetchTransitions, resetTransitions } from '../../../common/actions/transitions-activity-actions';
import { receiptInternalNotesSelector } from '../../../common/selectors/internal-notes-selectors';
import {
    transitionPurchaseOrderIntoCredit,
    transitionPurchaseOrderIntoInvoice,
} from '../../convert-receipt/transition-actions';
import { useAppSelector } from '../../../store/use-appstate-selector';
import { usePurchaseOrderDetailContext } from '../containers/context/purchase-order-context';
import { withPaloTheme } from '../../../common/components/palo/with-palo-theme';

// Module scope on purpose: getSteps() runs on every render, and a wrapper
// created there would be a new component type each time, remounting the step.
const PaloThemedPositions = withPaloTheme(PurchaseOrderDetailPositions);

/**
 * The positions, drawn in the place the head keeps for them under the supplier.
 *
 * They stay a step of their own — their state, their form and their validation
 * are unchanged, and one save still writes head and positions together. Only
 * where they are drawn moves, out of the foot of the mask into the middle of
 * it, the way the invoice has it (#2208).
 *
 * Nothing is drawn until the head has offered the place; that is the same
 * render, so there is no moment in which the positions stand anywhere else.
 */
const PaloSlottedPositions = (props: ComponentProps<typeof PaloThemedPositions>): ReactElement => (
    <PaloInPositionsSlot>
        <PaloThemedPositions {...props} />
    </PaloInPositionsSlot>
);

const styles = (theme: Theme) =>
    createStyles({
        spinner: {
            flex: 1,
        },
        container: {
            display: 'flex',
            overflow: 'hidden',
            height: '100%',
            flex: 1,
            flexDirection: 'column',
        },
        scrollContent: {
            marginTop: 2,
            flex: 1,
        },
        footer: {
            backgroundColor: '#fff',
            padding: theme.spacing(3, theme.general.pageContentSpacing),
        },
        ctaButton: {
            float: 'right',
            marginLeft: theme.spacing(2),
        },
    });

interface PurchaseOrderDetailProps extends WithStyles<typeof styles>, WithRouterProps {
    transitionsFromCount: number;
    activityCount: number;
    noteCount: number;
    attachmentCount: number;
    entity?: PurchaseOrder;
    defaultCurrency: string;
    pdfLink: string;
    xmlDownloadLink: string;
    user: User;

    resetUploads: () => void;
    fetchActivities: (docType: DocumentType, docId: number) => void;
    resetActivities: () => void;
    fetchTransitions: (docType: DocumentType, docId: number) => void;
    resetTransitions: () => void;
    fetchAllTaxRates: () => void;

    fetchPurchaseOrder: (id: number) => void;
    createOrUpdatePurchaseOrder: (purchaseOrder: Partial<PurchaseOrder>) => void;
    reportPurchaseOrderSaveFailed: () => void;
    resetPurchaseOrderState: () => void;
    deletePurchaseOrder: (id: number) => void;
    completePurchaseOrder: (purchaseOrder: Partial<PurchaseOrder> & ObjectWithId) => void;
    updateReceiptAlert: (id: number, alert: ReceiptAlert) => void;
    deleteReceiptAlert: (id: number) => void;
    transitionPurchaseOrderIntoInvoice: (transitionObject: PurchaseOrderTransition) => Promise<ConvertOutcome>;
    transitionPurchaseOrderIntoCredit: (transitionObject: PurchaseOrderTransition) => Promise<ConvertOutcome>;

    setDocumentType: (documentType: DocumentType) => void;
    resetDocumentType: () => void;

    fetchReceiptInternalNotes: (noteType: ReceiptNoteType, entityId: number) => void;
    resetReceiptInternalNotes: () => void;
}

interface PurchaseOrderDetailState {
    index: number;
    showCancelWarning: boolean;
    showOrderWarning: boolean;
    ctaButtonEnabled: boolean;
    // data can be different per step
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ctaButtonData: any;
    unlocked: boolean;
    paramsId?: number;
    previewVisible: boolean;
}

class PaloPurchaseOrderDetailBase extends Component<PurchaseOrderDetailProps, PurchaseOrderDetailState> {
    private isNew = true;
    private showPdfAfterSave = false;
    private resourceId?: number;
    private resource?: Partial<PurchaseOrder>;
    private stepsDone: number[] = [];
    private isLoading = false;

    public state: PurchaseOrderDetailState = {
        index: 0,
        showCancelWarning: false,
        showOrderWarning: false,
        ctaButtonEnabled: false,
        ctaButtonData: undefined,
        unlocked: false,
        paramsId: undefined,
        previewVisible: false,
    };

    public container: HTMLDivElement | null = null;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private headerRef = createRef<any>();

    private handleCancelOrder = () => {
        this.setState({ showCancelWarning: true });
    };

    private handleCancelOrderConfirmed = () => {
        const entity = this.props.entity;
        if (entity) {
            this.props.createOrUpdatePurchaseOrder({ ...entity, status: DocumentStatus.Canceled });
        }
        this.setState({ showCancelWarning: false });
    };

    /**
     * Whether the order has anything to order.
     *
     * The answer used to come from a helper that is rendered inside the
     * positions tab, so on every other tab the button stood greyed out with
     * "Die Bestellung hat noch keine Position" although the order had four
     * (R-137) — the action bar sits in the fixed head now and is there on
     * every tab, which the helper was never written for. The document is
     * asked instead: what the user is editing where it is to hand, and
     * otherwise what was loaded.
     */
    private hasPositions = (): boolean => {
        const edited = this.resource?.purchaseOrderItems;

        return (edited ?? this.props.entity?.purchaseOrderItems ?? []).length > 0;
    };

    /**
     * Whether anything was typed in this visit.
     *
     * The steps report their values upward as they change, so nothing
     * reported means nothing changed — and then there is nothing to save
     * before the order is completed or its PDF is drawn. Saving anyway was
     * what asked for a document that was never edited (R-137).
     */
    private hasEdits = (): boolean =>
        undefined !== this.resource && (!this.state.ctaButtonData || this.state.ctaButtonData.needsSave);

    private handleFinishOrder = async () => {
        const needsSave = this.hasEdits();
        if (await this.headerRef.current?.canActivateAfterTriedToActivate()) {
            if (needsSave) {
                this.handleSave();
            }
            this.setState({ showOrderWarning: true });
        }
    };

    /**
     * What the document's status allows — completing, cancelling, or nothing.
     * This is what the F and R keys go by.
     *
     * Deliberately narrower than the flow button, which also asks for its own
     * step and for the stepper's ready flag. A shortcut exists to save the walk
     * to that step, so it must not demand the walk first; whether the document
     * is fit to be completed is then checked where it belongs — both handlers
     * validate and save before they ask anything.
     */
    private flowAction = (): 'finish' | 'cancel' | undefined => {
        // From the entity, not from this.resource: the latter is a plain field
        // the step handover writes without a re-render, so a key bound to it
        // would keep whatever state it had when the mask last drew itself.
        const status = this.props.entity?.status;

        if (undefined === status) {
            return undefined;
        }

        if (DocumentStatus.Canceled === status) {
            return undefined;
        }

        return [
            DocumentStatus.Completed,
            DocumentStatus.CompletedWithTransition,
            DocumentStatus.CompletedWithoutTransition,
        ].includes(status)
            ? 'cancel'
            : 'finish';
    };

    /**
     * The order's own next step, beside "Speichern".
     *
     * "Speichern & Weiter" is gone with the wizard: the steps are tabs, there
     * is no next one to be sent to, and saving is the button next door (#2207).
     */
    /**
     * Completing the order — the step that moves it on, so it is the filled
     * action once there is nothing left to save (FRM-18). Cancelling a
     * completed order is not a step forward and has moved into the menu.
     */
    private renderFlowActions = (phase: PaloDocumentPhase) => {
        if ('draft' !== phase) {
            return null;
        }

        const withoutPositions = !this.hasPositions();

        return (
            <PaloFlowAction
                id="purchase-order-start-button"
                phase={phase}
                kind="advance"
                onClick={this.handleFinishOrder}
                // Not "there is something to save": an order that is already
                // as it should be is exactly the one to be completed, and
                // asking for edits first left the button dead on a tab nobody
                // had typed in (R-137).
                disabled={withoutPositions || !this.props.entity?.id}
                // Only when that is what stands in the way: the sentence used
                // to be printed under every disabled button, whatever the
                // reason (R-137).
                blockedReason={
                    withoutPositions ? (
                        <FormattedMessage id="app.purchaseOrders.detail.completeNeedsPosition" />
                    ) : undefined
                }
            >
                <FormattedMessage
                    id="app.menu.action.complete"
                    values={{ value: <FormattedMessage id="app.document.purchaseOrder" /> }}
                />
            </PaloFlowAction>
        );
    };

    private handleShowPdf = async () => {
        const needsSave = this.hasEdits();
        if (await this.headerRef.current?.canActivateAfterTriedToActivate()) {
            if (needsSave) {
                this.showPdfAfterSave = true;
                this.handleSave();
            } else {
                this.setState({ previewVisible: true });
            }
        }
    };

    private handleOrderConfirmed = () => {
        if (this.resource && this.resourceId) {
            this.props.completePurchaseOrder({
                ...this.resource,
                id: this.resourceId,
            });
            this.setState({ showOrderWarning: false });
        }
    };

    public handleAlertChange = (alert: ReceiptAlert | null) => {
        if (this.props.entity?.id) {
            if (!alert) {
                this.props.deleteReceiptAlert(this.props.entity.id);
            } else {
                this.props.updateReceiptAlert(this.props.entity.id, alert);
            }
        }
    };

    private createOrUpdateOrder() {
        if (!this.resource) {
            this.props.reportPurchaseOrderSaveFailed();

            return;
        }

        this.props.createOrUpdatePurchaseOrder(this.resource);
    }

    // TODO: Check typings
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private handleGlobalStateChange = (purchaseOrder: any) => {
        this.resource = purchaseOrder as Partial<PurchaseOrder>;
    };

    private handleSave = () => {
        this.createOrUpdateOrder();
    };

    private handleIndexChange = (index: number) => {
        this.setState({ index });
    };

    private handleDelete = async () => {
        if (this.resourceId) {
            await this.props.deletePurchaseOrder(this.resourceId);
            this.props.navigate(-1);
        }
    };

    private setDoneSteps = (entity: PurchaseOrder): number[] => {
        this.stepsDone = [];

        if (entity.customerAccount && entity.supplierAccount) {
            this.stepsDone.push(PurchaseOrderSteps.Overview);
        }

        if (entity.purchaseOrderItems && entity.purchaseOrderItems.length > 0) {
            this.stepsDone.push(PurchaseOrderSteps.Positions);
        }

        return this.stepsDone;
    };

    private handleUnlockedChange = (unlocked: boolean) => {
        this.setState({ unlocked });
    };

    public componentDidMount() {
        // Force another render when the portal ref has been updated
        this.forceUpdate();
    }

    public UNSAFE_componentWillMount() {
        this.props.resetPurchaseOrderState();
        this.props.resetUploads();
        this.props.resetActivities();
        this.props.resetTransitions();
        this.props.resetReceiptInternalNotes();
        this.props.setDocumentType(DocumentType.PurchaseOrder);
        this.props.fetchAllTaxRates();

        if (this.props.params['id'] === 'new') {
            return;
        }
        this.isNew = false;
        this.isLoading = true;
        this.resourceId = +this.props.params['id'];
        if (this.resourceId) {
            this.props.fetchPurchaseOrder(this.resourceId);
        }
        if (this.props.entity) {
            this.setDoneSteps(this.props.entity);
        }
    }

    public componentWillUnmount() {
        this.props.resetPurchaseOrderState();
        this.props.resetUploads();
        this.props.resetActivities();
        this.props.resetTransitions();
        this.props.resetDocumentType();
        this.props.resetReceiptInternalNotes();
    }

    public UNSAFE_componentWillReceiveProps(nextProps: PurchaseOrderDetailProps) {
        const paramsId = +nextProps.params['id'];
        // The route holds "new" while a document is being created, and a number
        // never equals itself once it is NaN: every update then counted as a
        // change of document and asked the server for /purchase-orders/NaN,
        // three times over, each answered with an error the reader could do
        // nothing with (#2210, R-04). Compare what the route says, and only
        // fetch what can be fetched.
        const routeChanged = nextProps.params['id'] !== this.props.params['id'];

        if (routeChanged && !this.isNew && Number.isFinite(paramsId)) {
            this.props.fetchPurchaseOrder(paramsId);
            this.props.fetchActivities(DocumentType.PurchaseOrder, paramsId);
            this.props.fetchTransitions(DocumentType.PurchaseOrder, paramsId);
            return;
        }

        // Only for another copy of the order. Every change of props reran the
        // requests below, and set the files back to those the order was
        // loaded with: a file just uploaded vanished until the page was
        // reloaded (R-193). The files come with the server's copy now.
        if (nextProps.entity && nextProps.entity !== this.props.entity) {
            if (this.isNew) {
                nextProps.navigate(
                    nextProps.location.pathname.replace(nextProps.params['id'], nextProps.entity.id.toString()),
                    { replace: true }
                );
                this.resourceId = nextProps.entity.id;
                this.isNew = false;
            }
            if (this.showPdfAfterSave) {
                this.showPdfAfterSave = false;
                this.setState({ previewVisible: true });
            }
            this.isLoading = false;
            this.props.fetchActivities(DocumentType.PurchaseOrder, nextProps.entity.id);
            this.props.fetchTransitions(DocumentType.PurchaseOrder, nextProps.entity.id);
            this.props.fetchReceiptInternalNotes(ReceiptNoteType.PurchaseOrders, nextProps.entity.id);
            this.setDoneSteps(nextProps.entity);
        }

        this.setState({ paramsId });
    }

    // data can be different per step
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private handleCtaButtonStateChange = (ctaButtonEnabled: boolean, ctaButtonData: any) => {
        this.setState({ ctaButtonEnabled, ctaButtonData });
    };

    private handleCancelWarningClose = () => {
        this.setState({ showCancelWarning: false });
    };

    private handleOrderWarningClose = () => {
        this.setState({ showOrderWarning: false });
    };

    private getSteps = (): PoolStep[] => {
        return [
            {
                name: <FormattedMessage id="app.purchaseOrders.detail.step.overview" />,
                component: PaloPurchaseOrderDetailOverview,
                tab: 'document',
            },
            {
                // Same tab as the head: an order is written on one page, and
                // the first article no longer waits for a save (#2208).
                name: <FormattedMessage id="app.purchaseOrders.detail.step.positions" />,
                component: PaloSlottedPositions,
                tab: 'document',
            },
            {
                name: <PaloDocumentChainTabName />,
                component: PaloDocumentChain,
                readOnly: true,
                // A document being created is not part of anything yet.
                isActive: !!this.props.entity?.id,
            },
        ];
    };

    private handleTransition = (transitionObject: Transition, target: TransitionTarget) =>
        TransitionTarget.Invoice === target
            ? this.props.transitionPurchaseOrderIntoInvoice(transitionObject as PurchaseOrderTransition)
            : this.props.transitionPurchaseOrderIntoCredit(
                  changePurchaseOrderToCredit(transitionObject as PurchaseOrderTransition)
              );

    public render() {
        const { classes, entity, user, defaultCurrency } = this.props;
        const { index, unlocked, showCancelWarning, showOrderWarning } = this.state;

        const converterAvailable =
            entity &&
            (entity.status === DocumentStatus.Completed ||
                entity.status === DocumentStatus.CompletedWithTransition ||
                entity.status === DocumentStatus.CompletedWithoutTransition);
        const currency = (entity && entity.currency) || defaultCurrency;
        // No unlocked state to honour: a closed order stays closed for every
        // role, so the phase follows its status alone (#2217).
        const phase = paloDocumentPhase(entity?.status, { saved: !!entity?.id });

        return (
            <PoolSpinner enabled={this.isLoading} sx={{ display: 'block', height: '100%' }}>
                <PurchaseOrderDetailProvider>
                    <PaloPositionsSlotProvider>
                        {/* Lends the "Belege" tab the document a follow-up is made
                        from, and the handler that makes it (#2166). */}
                        {entity && <PaloDocumentChainCreate source={entity} onConvert={this.handleTransition} />}
                        <PaloDocumentTitle
                            typeId="app.document.purchaseOrder"
                            number={this.state.paramsId ? padZeroLeft(this.state.paramsId, 5) : undefined}
                        />
                        <PaloDetailHeader
                            poolRef={this.headerRef}
                            statusTranslationId={getPurchaseOrderStatusTranslationId}
                            steps={this.getSteps()}
                            stepsDone={this.stepsDone}
                            // A saved order is looked at as much as filled in, so its
                            // steps open in any order; a new one keeps its path.
                            enforceStepOrder={!entity?.id}
                            stepIndex={index}
                            contentPortal={this.container}
                            stepperInitialState={entity}
                            entity={entity}
                            isSuperAdmin={hasRole(UserRole.SuperAdmin, user)}
                            unlocked={unlocked}
                            comparesToServer={true}
                            unlockable={false}
                            currency={currency}
                            converterAvailable={converterAvailable}
                            title="app.document.purchaseOrder"
                            connectorWidth="10%"
                            fallbackUrl={ROUTES.purchaseOrders}
                            preview={{
                                onOpen: () => void this.handleShowPdf(),
                                disabled: !entity?.id || !this.state.ctaButtonEnabled,
                                disabledHint: <FormattedMessage id="app.common.previewAfterSave" />,
                            }}
                            menuItems={[
                                // The order prints its own way — it saves first —
                                // so it keeps its entry here rather than taking the
                                // header's. Printing is not the next step of the
                                // document and stands with the rest (#2206).
                                {
                                    label: <FormattedMessage id="app.common.previewPrint" />,
                                    disabled: !entity?.id || !this.state.ctaButtonEnabled,
                                    onClick: () => {
                                        void this.handleShowPdf();
                                    },
                                },
                                {
                                    label: <FormattedMessage id="app.common.downloadXml" />,
                                    disabled: !entity?.id,
                                    onClick: () => window.open(this.props.xmlDownloadLink, '_blank'),
                                },
                                ...('done' === phase
                                    ? [
                                          {
                                              label: (
                                                  <FormattedMessage
                                                      id="app.menu.action.cancel"
                                                      values={{
                                                          value: <FormattedMessage id="app.document.purchaseOrder" />,
                                                      }}
                                                  />
                                              ),
                                              onClick: this.handleCancelOrder,
                                              danger: true,
                                          },
                                      ]
                                    : []),
                            ]}
                            breadcrumbs={[
                                {
                                    label: <FormattedMessage id="app.navigation.purchaseOrders" />,
                                    onClick: () => this.props.navigate(ROUTES.purchaseOrders),
                                },
                            ]}
                            pager={
                                <PaloRecordPager
                                    currentId={entity?.id}
                                    onOpen={id => this.props.navigate(`${ROUTES.purchaseOrders}/${id}`)}
                                />
                            }
                            flowActions={this.renderFlowActions(phase)}
                            onUnlockedChange={this.handleUnlockedChange}
                            onGlobalStateChange={this.handleGlobalStateChange}
                            onIndexChange={this.handleIndexChange}
                            onManualSave={this.handleSave}
                            onDelete={this.handleDelete}
                            onCtaButtonChanged={this.handleCtaButtonStateChange}
                            onTransition={this.handleTransition}
                            onAlertChange={this.handleAlertChange}
                            dock={
                                // Nothing to hang on a document that does not exist
                                // yet; the form gets the width instead (#2208).
                                !entity?.id ? undefined : (
                                    <PaloRecordDock
                                        count={this.props.attachmentCount}
                                        attachments={
                                            <PaloPurchaseOrderAttachments
                                                supplierReadOnly={
                                                    !unlocked && !(entity && entity.status === DocumentStatus.Created)
                                                }
                                            />
                                        }
                                    />
                                )
                            }
                            history={
                                // Nothing to look back on before the first save.
                                entity?.id ? (
                                    <PaloHistory>
                                        <DetailStream
                                            documentType={DocumentType.PurchaseOrder}
                                            documentId={entity.id}
                                        />
                                    </PaloHistory>
                                ) : undefined
                            }
                        >
                            <div ref={ref => (this.container = ref)} />
                        </PaloDetailHeader>
                    </PaloPositionsSlotProvider>
                </PurchaseOrderDetailProvider>
                <PaloDocumentPreview
                    url={this.state.previewVisible ? this.props.pdfLink : undefined}
                    title={<FormattedMessage id="app.common.preview" />}
                    openLabel={<FormattedMessage id="app.common.openInNewTab" />}
                    closeLabel={<FormattedMessage id="app.common.close" />}
                    onClose={() => this.setState({ previewVisible: false })}
                />
                <PaloStatusChangeDialog
                    open={showCancelWarning}
                    onClose={this.handleCancelWarningClose}
                    title={
                        <FormattedMessage
                            id="app.menu.action.cancel"
                            values={{ value: <FormattedMessage id="app.document.purchaseOrder" /> }}
                        />
                    }
                    description={<FormattedMessage id="app.purchaseOrders.detail.cancelWarning" />}
                    confirmLabel={<FormattedMessage id="app.common.yes" />}
                    destructive={true}
                    documentType={DocumentType.PurchaseOrder}
                    documentId={entity?.id}
                    onConfirm={this.handleCancelOrderConfirmed}
                />
                <PaloShortcutBinding
                    combo="f"
                    group="Beleg"
                    label="Abschließen"
                    disabled={'finish' !== this.flowAction()}
                    handler={() => {
                        void this.handleFinishOrder();
                    }}
                />
                <PaloShortcutBinding
                    combo="r"
                    group="Beleg"
                    label="Stornieren"
                    disabled={'cancel' !== this.flowAction()}
                    handler={this.handleCancelOrder}
                />
                <PaloCompleteDocumentDialog
                    open={showOrderWarning}
                    onClose={this.handleOrderWarningClose}
                    onConfirm={this.handleOrderConfirmed}
                    resourceNumber={this.resource?.number}
                    titleId="app.purchaseOrders.detail.finish"
                    contentMessageId="app.purchaseOrders.detail.orderWarning"
                    documentType={DocumentType.PurchaseOrder}
                    documentId={entity?.id}
                />
            </PoolSpinner>
        );
    }
}

const PurchaseOrderDetailWithRouter = withRouter(PaloPurchaseOrderDetailBase);

const mapStateToProps = (state: AppState) => ({
    transitionsFromCount: state.transitionActivities.transitionsFromCount,
    activityCount: activitiesSelector(state).length,
    noteCount: receiptInternalNotesSelector(state).length,
    attachmentCount: supplierMediaSelector(state).length + internalMediaSelector(state).length,
    entity: state.purchaseOrders.entity,
    defaultCurrency: state.settings.currency,
    pdfLink:
        (state.purchaseOrders.entity &&
            urljoin(API_BASE_URL, `/purchase-orders/${state.purchaseOrders.entity.id}/pdf-purchase-order`)) ||
        '',
    xmlDownloadLink:
        (state.purchaseOrders.entity &&
            urljoin(API_BASE_URL, `/purchase-orders/${state.purchaseOrders.entity.id}/supplier-xml`)) ||
        '',
    user: state.user,
});

export const ConnectedPurchaseOrderDetail = connect(mapStateToProps, {
    createOrUpdatePurchaseOrder,
    reportPurchaseOrderSaveFailed,
    deletePurchaseOrder,
    completePurchaseOrder,
    fetchPurchaseOrder,
    resetPurchaseOrderState,
    resetUploads,
    fetchActivities,
    resetActivities,
    transitionPurchaseOrderIntoInvoice,
    transitionPurchaseOrderIntoCredit,
    fetchTransitions,
    resetTransitions,
    updateReceiptAlert,
    deleteReceiptAlert,
    setDocumentType,
    resetDocumentType,
    fetchReceiptInternalNotes,
    resetReceiptInternalNotes,
    fetchAllTaxRates,
})(PurchaseOrderDetailWithRouter);

const StyledPurchaseOrderDetail = withStyles(styles)(ConnectedPurchaseOrderDetail);

/** One wizard step is mounted at a time, so a single flag marks the open one. */
export const PaloPurchaseOrderDetail = (props: Record<string, unknown>): ReactElement => {
    // The order as the server last handed it over. When a different one
    // arrives, every unsaved-changes mark goes — that is the moment the mask is
    // provably saved. Without it the marks from writing the order survived the
    // save, and the mask's own step to the new number was stopped by its own
    // "unsaved changes" dialog (#2210, R-04).
    const loadedDocument = useAppSelector(state => state.purchaseOrders.entity);

    return (
        <PaloDirtyProvider loadedDocument={loadedDocument}>
            <PaloDocumentChainProvider documentType={DocumentType.PurchaseOrder}>
                <StyledPurchaseOrderDetail {...props} />
            </PaloDocumentChainProvider>
        </PaloDirtyProvider>
    );
};
